/* eslint-disable */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { customAxios } from "lib/axios";
import { User } from "types";
import storage from "utils/storage";
import { useAddLeaveBalance } from "api/newLeave/balanceUpdate";
import { fetchUserPermissions } from "api/permission/getpermission";
type UserResponse = {
  token: string;
  user: User;
};

type LoginData = {
  email: string;
  password: string;
};

type RegisterData = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  organizationId: string;
};

type CreateUserInternally = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  role: string;
  position: string;
  org: string;
  programs?: string[]; // Array of program IDs to assign to the user
};

type CreateUserInternallyResponse = {
  user: User;
};
const login = async (loginData: LoginData): Promise<UserResponse> => {
  const { data } = await customAxios.post<UserResponse>(
    "/users/login",
    loginData
  );
  return data;
};

const register = async (registerData: RegisterData) => {
  const { data } = await customAxios.post(
    "/users/external-register",
    registerData
  );
  return data;
};

const loginAsGuest = async (): Promise<UserResponse> => {
  const { data } = await customAxios.post<UserResponse>("/users/demo", {
    mode: "demo",
  });
  return data;
};

const createUserInternally = async (
  userData: CreateUserInternally
): Promise<CreateUserInternallyResponse> => {
  const { data } = await customAxios.post<UserResponse>(
    "/users/createuser",
    userData
  );
  return data;
};

export const useAuth = () => {
  const queryClient = useQueryClient();
  const addLeaveBalanceMutation = useAddLeaveBalance();
  const buildLeaveBalancePayload = (u: any) => {
    const userId = String(u?._id ?? u?.id ?? "");
    const orgId = String(u?.org?._id ?? u?.org ?? u?.orgId ?? "");
    return {
      _id: userId,
      firstName: u?.firstName ?? u?.name ?? "",
      lastName: u?.lastName ?? "",
      email: u?.email ?? "",
      org: orgId,
    };
  };
  const prefetchPermissionsForUser = (userId?: string) => {
    if (!userId) return;

    void queryClient
      .prefetchQuery(
        ["userPermissions", userId],
        () => fetchUserPermissions(userId),
        {
          staleTime: 1000 * 60 * 60 * 24,
          cacheTime: 1000 * 60 * 60 * 24,
          retry: 1,
        }
      )
      .catch(() => {});
  };
  const loginMutation = useMutation({
    mutationFn: login,
    onSuccess: async (data) => {
      const { token, user } = data;
      storage.setToken(token);
      queryClient.setQueryData(["auth-user"], user);
      try {
        const payload = buildLeaveBalancePayload(user);
        if (payload._id) {
          addLeaveBalanceMutation.mutate(payload);
        }
        await prefetchPermissionsForUser(String(user?._id ?? ""));

        try {
          const perms = await fetchUserPermissions(String(user._id));
          queryClient.setQueryData(
            ["userPermissions", String(user._id)],
            perms,
            {
              updatedAt: Date.now(),
            }
          );
        } catch {
          /* ignore */
        }
      } catch (e) {}
    },
  });

  const registerMutation = useMutation({
    mutationFn: register,
    // onSuccess: (data) => {
    //   const { token, user } = data;
    //   storage.setToken(token);
    //   queryClient.setQueryData(["auth-user"], user);
    // },
  });

  const logout = () => {
    storage.clearToken();
    storage.clearAll();
    storage.clearOriginalToken();
    queryClient.removeQueries(["userPermissions"]);
    queryClient.clear();
  };

  const loginAsGuestMutation = useMutation({
    mutationFn: loginAsGuest,
    onSuccess: (data) => {
      const { token, user } = data;
      storage.setToken(token);
      queryClient.setQueryData(["auth-user"], user);
    },
  });

  const createUserMutation = useMutation({
    mutationFn: createUserInternally,
    onSuccess: (data) => data,
  });
  return {
    login: loginMutation.mutate,
    isLoggingIn: loginMutation.isLoading,
    register: registerMutation.mutate,
    isRegistering: registerMutation.isLoading,
    logout,
    loginAsGuest: loginAsGuestMutation.mutate,
    isLoggingInAsGuest: loginAsGuestMutation.isLoading,
    createUser: createUserMutation.mutate,
  };
};
